import React, { PropsWithChildren, PureComponent } from 'react';
import { Menu, Popover } from 'antd';
import { TooltipPlacement } from 'antd/es/tooltip';
import { RenderFunction } from 'antd/lib/_util/getRenderPropValue';
import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { MenuItemsFunction } from 'app/components/dashboard/common/MenuUtils';

interface PopoverMenuProps {
    menuItems: ItemType[] | MenuItemsFunction; //it needs to have a function as property so it would be possible to pass menu items that can be changed along the time (need to be rerender) (example: usage of the static NetworkMonitoringFlow)
    onMenuItemClick?: (key: string) => void;
    title?: React.ReactNode | RenderFunction;
    placement?: TooltipPlacement; // default is 'right'
}

interface PopoverMenuState {
    visible: boolean;
}

/**
 * This component combines a popover and a menu.
 * It requires some extra logic to be put in place, in order to support the following features:
 *   1. Hide popover when menu item clicked
 *   2. Hide popover when use clicks outside the popover
 *
 * First feature requires to control visibility on the popover component, which has the effect to disable the
 * second feature that exists in the uncontrolled version of the popover component.
 *
 * Reference is kept on wrapper divs, in order to know when a click is performed outside of the popover
 * and outside of the component that is used to trigger display of the popover (very likely a button)
 *
 */
export class PopoverMenu extends PureComponent<PropsWithChildren<PopoverMenuProps>, PopoverMenuState> {
    buttonWrapperRef = React.createRef<HTMLDivElement>();
    menuWrapperRef = React.createRef<HTMLDivElement>();
    state = {
        visible: false,
    };
    handleClickOutside = event => {
        if (!this.menuWrapperRef?.current || !this.buttonWrapperRef?.current) return;

        const parentNode = event.target.parentNode;
        // In order to use the parent.contains(child) on DOM node, the wrapperRef must not point to a
        // React Component (deprecated since React 16), but should point to an HTMLElement instead.
        // submenus (popups) are NOT added to the menu wrapper div and so the this.menuWrapperRef.current.contains fails, the workaround was to check if the target parent is a menuitem
        if (
            !this.buttonWrapperRef.current.contains(event.target) &&
            !this.menuWrapperRef.current.contains(event.target) &&
            'menuitem' !== parentNode['role']
        ) {
            this.setVisible(false);
        }
    };
    //add and remove event listener with options true because was not reaching the documents event listeners due to event being stopped from bubbling.
    //more at: https://www.robinwieruch.de/react-hook-detect-click-outside-component/
    setVisible = (visible: boolean) => {
        const callback = visible
            ? () => document.addEventListener('click', this.handleClickOutside, { capture: true })
            : () => document.removeEventListener('click', this.handleClickOutside, { capture: true });
        this.setState({ visible: !this.state.visible }, callback);
    };
    toggleVisible = () => {
        this.setVisible(!this.state.visible);
    };
    handleMenuItemClick = e => {
        this.setVisible(!this.state.visible);
        this.props.onMenuItemClick?.(e.key);
    };
    renderChildren = () => {
        // handle Click on child element, to make the popover visible/hidden
        return (
            this.props.children &&
            React.Children.map(this.props.children, (child, index) => {
                if (React.isValidElement(child)) {
                    //passing directly the props to the clone element (2nd arg) was giving a typescript error:
                    // Argument of type '{ key: number; onClick: () => void; }' is not assignable to parameter of type 'Partial<unknown> & Attributes'.
                    //       Object literal may only specify known properties, and 'onClick' does not exist in type 'Partial<unknown> & Attributes'.
                    const props = { key: index, onClick: () => this.toggleVisible() };
                    return React.cloneElement(child, props);
                }
                return child;
            })
        );
    };
    renderContent = () => {
        if (!this.state?.visible) return null;

        return (
            <div ref={this.menuWrapperRef}>
                <Menu
                    items={typeof this.props.menuItems === 'function' ? this.props.menuItems() : this.props.menuItems}
                    mode="vertical"
                    onClick={this.handleMenuItemClick}
                />
            </div>
        );
    };

    render() {
        return (
            <Popover
                open={this.state.visible}
                placement={this.props.placement || 'right'}
                title={this.props.title}
                content={this.renderContent}>
                <div ref={this.buttonWrapperRef}>{this.renderChildren()}</div>
            </Popover>
        );
    }
}
